import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { projectsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import ProjectDetailModal from './ProjectDetailModal';

interface Project {
  id: string;
  name: string;
  code: string;
  description: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  city: { id: string; name: string };
  sites: any[];
  createdAt: string;
  _count?: {
    sites: number;
    workItems: number;
    projectVehicles: number;
    projectPersonnel: number;
  };
}

interface City {
  id: string;
  name: string;
  code?: string;
}

interface District {
  id: string;
  name: string;
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
  CANCELLED: 'bg-danger bg-opacity-10 text-danger',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
  CANCELLED: 'İptal',
};

const ProjectList: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState<Project[]>([]);
  const [cities, setCities] = useState<City[]>([]); // İller
  const [districts, setDistricts] = useState<District[]>([]); // İlçeler
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    provinceId: '', // İl ID
    districtId: '', // İlçe ID (opsiyonel)
    status: 'ACTIVE',
    startDate: '',
    endDate: '',
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const openDetailModal = (project: Project, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedProject(project);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedProject(null);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, citiesRes] = await Promise.all([
        projectsAPI.getAll(statusFilter ? { status: statusFilter } : undefined),
        projectsAPI.getCities(),
      ]);
      let filteredProjects = projectsRes.data;
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        filteredProjects = filteredProjects.filter((p: Project) =>
          p.name.toLowerCase().includes(s) ||
          p.code.toLowerCase().includes(s) ||
          p.city.name.toLowerCase().includes(s)
        );
      }
      setProjects(filteredProjects);
      setCities(citiesRes.data);
      // Açık modal varsa güncel veriyle güncelle
      if (selectedProject) {
        const updated = projectsRes.data.find((p: Project) => p.id === selectedProject.id);
        if (updated) setSelectedProject(updated);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, searchFilter]);

  // İl seçilince ilçeleri yükle
  const handleProvinceChange = async (provinceId: string) => {
    setFormData({ ...formData, provinceId, districtId: '' });
    setDistricts([]);

    if (provinceId) {
      try {
        setLoadingDistricts(true);
        const res = await projectsAPI.getDistricts(provinceId);
        setDistricts(res.data);
      } catch (error) {
        console.error('İlçeler yüklenemedi:', error);
      } finally {
        setLoadingDistricts(false);
      }
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setFormData({ name: '', code: '', description: '', provinceId: '', districtId: '', status: 'ACTIVE', startDate: '', endDate: '' });
      setDistricts([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code || !formData.provinceId) {
      toast.error('Tüm alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      // İlçe seçildiyse ilçe ID'sini, seçilmediyse il ID'sini gönder
      const cityId = formData.districtId || formData.provinceId;
      const submitData: any = {
        name: formData.name,
        code: formData.code,
        cityId,
        status: formData.status,
      };
      if (formData.description) submitData.description = formData.description;
      if (formData.startDate) submitData.startDate = formData.startDate;
      if (formData.endDate) submitData.endDate = formData.endDate;

      await projectsAPI.create(submitData);
      toast.success('Proje oluşturuldu');
      toggleModal();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Projeler</h4>
            <div className="page-title-right d-flex align-items-center gap-2 flex-wrap">
              <Input
                type="text"
                bsSize="sm"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Ara..."
                style={{ width: '150px' }}
              />
              <Input
                type="select"
                bsSize="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="">Tüm Durumlar</option>
                <option value="ACTIVE">Aktif</option>
                <option value="COMPLETED">Tamamlandı</option>
                <option value="SUSPENDED">Askıda</option>
                <option value="CANCELLED">İptal</option>
              </Input>
              {isAdmin && (
                <Button color="primary" onClick={toggleModal}>
                  <i className="ri-add-line me-1"></i>
                  Yeni Proje
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Proje Listesi</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">Henüz proje bulunmuyor.</p>
                  </div>
                ) : isMobile ? (
                  // Mobile Card View
                  <div className="admin-card-list">
                    {projects.map((project) => (
                      <div key={project.id} className="admin-card-item">
                        {/* Header: Project Name + Status - Clickable */}
                        <div
                          className="admin-card-header"
                          onClick={() => openDetailModal(project)}
                          style={{ cursor: 'pointer' }}
                        >
                          <h6 className="admin-card-title">{project.name}</h6>
                          <span
                            className={`px-2 py-1 rounded-pill small ${statusStyles[project.status]}`}
                            style={{ fontWeight: 500 }}
                          >
                            {statusLabels[project.status]}
                          </span>
                        </div>

                        {/* Body: Project Details - Clickable */}
                        <div
                          className="admin-card-body"
                          onClick={() => openDetailModal(project)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-code-box-line"></i>Kod
                            </span>
                            <div className="admin-info-value value-highlight">
                              {project.code}
                            </div>
                          </div>
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-map-pin-line"></i>Şehir
                            </span>
                            <span className="admin-info-value">
                              {project.city.name}
                            </span>
                          </div>
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-building-line"></i>Şantiye
                            </span>
                            <span className="admin-info-value">
                              {project.sites?.length || 0} şantiye
                            </span>
                          </div>
                        </div>

                        {/* Footer: Actions */}
                        <div className="admin-card-footer">
                          <button
                            type="button"
                            className="admin-action-btn admin-btn-primary"
                            style={{ flex: 1 }}
                            onClick={(e) => openDetailModal(project, e)}
                          >
                            <i className="ri-eye-line"></i>
                            Detaylar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop Table View
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Proje Kodu</th>
                          <th>Proje Adı</th>
                          <th>Şehir</th>
                          <th>Şantiye Sayısı</th>
                          <th>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.map((project) => (
                          <tr
                            key={project.id}
                            onClick={() => openDetailModal(project)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <strong>{project.code}</strong>
                            </td>
                            <td>{project.name}</td>
                            <td>{project.city.name}</td>
                            <td>{project.sites?.length || 0} şantiye</td>
                            <td>
                              <span
                                className={`px-2 py-1 rounded-pill small ${statusStyles[project.status]}`}
                                style={{ fontWeight: 500 }}
                              >
                                {statusLabels[project.status]}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

      {/* Yeni Proje Modal */}
      <AdaptiveModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title="Yeni Proje"
        size="md"
        footer={
          <>
            <Button color="secondary" onClick={toggleModal}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="project-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="project-form" onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Proje Kodu *</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  placeholder="Örn: PRJ-001"
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>İl *</Label>
                <Input
                  type="select"
                  value={formData.provinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  required
                >
                  <option value="">İl Seçin</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.code ? `${city.code} - ${city.name}` : city.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>İlçe</Label>
                <Input
                  type="select"
                  value={formData.districtId}
                  onChange={(e) => setFormData({ ...formData, districtId: e.target.value })}
                  disabled={!formData.provinceId || loadingDistricts}
                >
                  <option value="">
                    {loadingDistricts ? 'Yükleniyor...' : 'İlçe Seçin (Opsiyonel)'}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Durum</Label>
                <Input
                  type="select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="ACTIVE">Aktif</option>
                  <option value="SUSPENDED">Askıda</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Proje Adı *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Proje adını girin"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Açıklama</Label>
            <Input
              type="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Proje hakkında kısa açıklama..."
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </AdaptiveModal>

      {/* Proje Detay Modal */}
      <ProjectDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        project={selectedProject}
        cities={cities}
        onUpdate={fetchData}
      />
    </>
  );
};

export default ProjectList;
